import { ReactNode, useEffect } from "react"

import useHonestChainWatchtower from "@hfc/shared/hooks/useHonestChainWatchtower"
import HonestNotification from "@hfc/shared/components/HonestNotification"
import isProduction from "@hfc/shared/utils/isProduction"

import { gsap } from "gsap/dist/gsap"
import Draggable from "gsap/dist/Draggable"
import useViewport from "@hfc/shared/hooks/window/useViewport"

interface RoomProps {
	room: ReactNode
	roomName?: string
	zoom: number
	shouldHideOverlay: boolean
	currentPath?: string
}

const Room = ({ room, roomName, zoom, shouldHideOverlay }: RoomProps) => {
	const { notification, chainConfig, chainId } = useHonestChainWatchtower()

	if (!isProduction())
		console.table({ ...chainConfig, actualNetwork: chainId })

	const isMap = roomName === "map"
	const extraRoomClassNames = isMap ? " " : ""
	const { type, widthPx, heightPx } = useViewport()

	useEffect(() => {
		gsap.registerPlugin(Draggable)
		let tween: gsap.core.Tween

		const id = "room"
		const element = document.getElementById(id)
		if (element) {
			if (!shouldHideOverlay) {
				let bounds = {}
				tween = gsap.to(`#${id}`, {
					x: 0,
					y: 0,
					transformOrigin: "center 20%",
				})
				if (type === "desktop") {
					bounds = {
						minX: -(widthPx / 2),
						minY: -(heightPx / 2),
						maxX: widthPx / 2,
						maxY: heightPx / 2,
					}
				} else {
					bounds = {
						minX: -(widthPx * 2),
						minY: -(heightPx * 2),
						maxX: widthPx / 2,
						maxY: heightPx / 2,
					}
				}

				Draggable.create(`#${id}`, {
					bounds: bounds,
					zIndexBoost: false,
					allowEventDefault: true,
					onDragEnd: () => {
						if (zoom <= 1) {
							const { endY, endX, maxY } = Draggable.get(`#${id}`)
							tween = gsap.to(`#${id}`, {
								x: type === "desktop" ? 0 : endX,
								y: endY < -maxY ? -maxY : 0,
								transformOrigin: "center 20%",
								ease: "slow(0.5, 0.5, false)",
							})
						}
					},
				})
				Draggable.zIndex = 1
			} else {
				gsap.to(`#${id}`, 0, {
					scale: 1,
					x: 0,
					y: 0,
					transformOrigin: "center 20%",
				})
				Draggable.get(`#${id}`)?.kill()
				element.style.transform = "none"
			}
		}

		return () => {
			if (tween) tween.kill()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [zoom, shouldHideOverlay])

	return (
		<>
			<div
				id="room"
				className={`box-border relative flex items-center justify-center w-screen min-h-screen my-auto overflow-auto hide-scroll ${extraRoomClassNames}`}
			>
				{room}
			</div>
			<HonestNotification
				showNotification={false}
				notification={notification}
			/>
		</>
	)
}

export default Room
